import React, { useState, useEffect, useMemo } from 'react';
import {
    SpaceBetween,
    Header,
    Container,
    Box,
    ColumnLayout,
    Select,
    DatePicker,
    Button,
    Spinner,
    Alert,
    Tabs,
    FormField,
    Multiselect,
    MultiselectProps,
} from '@amzn/awsui-components-react';
import CashPositioningAPIClient from './cashPositioningAPIClient';
import {
    AgentSummary,
    PositioningRegion,
    WireRecommendation,
    REGION_OPTIONS,
} from './types';
import WireRecommendationsTable from './WireRecommendationsTable';
import VariancesTable from './VariancesTable';
import AIQueryPanel from './AIQueryPanel';

/**
 * Cash Positioning Dashboard — main page component.
 * Layout matches the prototype:
 * - Filters section (Date, Region, Entity, Account) with Run button
 * - Summary cards (run status, wire counts, entity counts)
 * - Tabbed results (ZBA, AQ, Variances, AI)
 */
const CashPositioningDashboard: React.FC = () => {
    // Filter state — default to "ALL" selected
    const [region, setRegion] = useState<PositioningRegion>('APAC');
    const today = new Date();
    const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [date, setDate] = useState<string>(localDate);
    const [selectedEntities, setSelectedEntities] = useState<ReadonlyArray<MultiselectProps.Option>>([{ value: '__ALL__', label: 'ALL ENTITIES' }]);
    const [selectedAccounts, setSelectedAccounts] = useState<ReadonlyArray<MultiselectProps.Option>>([{ value: '__ALL__', label: 'ALL ACCOUNTS' }]);

    // Data state
    const [summary, setSummary] = useState<AgentSummary | null>(null);
    const [allRecommendations, setAllRecommendations] = useState<WireRecommendation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('zba');

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const summaryResp = await CashPositioningAPIClient.getAgentSummary(region, date);
            setSummary(summaryResp.summary || null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
        }

        // Fetch ZBA data to populate filter dropdowns
        try {
            const zbaResp = await CashPositioningAPIClient.getZbaPositioning(region, date);
            setAllRecommendations(zbaResp.recommendations || []);
        } catch (err) {
            // Non-fatal for filters
        } finally {
            setLoading(false);
        }
    };

    // Callback when the ZBA table loads its data — keeps filters in sync
    const handleZbaDataLoaded = (data: WireRecommendation[]) => {
        if (data.length > 0) {
            setAllRecommendations(data);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Filtered entity list to pass to child components
    // __ALL__ means no filter (show everything)
    const entityFilterValues = useMemo(() => {
        return selectedEntities.filter(e => e.value !== '__ALL__').map(e => e.value || '');
    }, [selectedEntities]);

    const accountFilterValues = useMemo(() => {
        return selectedAccounts.filter(a => a.value !== '__ALL__').map(a => a.value || '');
    }, [selectedAccounts]);

    // Derive entity and account options from loaded recommendations
    const entityOptions = useMemo(() => {
        const entities = new Set<string>();
        allRecommendations.forEach(r => {
            if (r.entityId) entities.add(r.entityId);
        });
        return Array.from(entities).sort().map(e => ({ value: e, label: e }));
    }, [allRecommendations]);

    const accountOptions = useMemo(() => {
        const accounts = new Set<string>();
        // Filter recommendations by selected entities (if any selected beyond ALL)
        const filteredRecs = entityFilterValues.length > 0
            ? allRecommendations.filter(r => entityFilterValues.includes(r.entityId || ''))
            : allRecommendations;

        filteredRecs.forEach(r => {
            // Get accounts from balanceBreakdown (account-level data) — preferred source
            if (r.balanceBreakdown && r.balanceBreakdown.length > 0) {
                r.balanceBreakdown.forEach(b => {
                    if (b.accountCode) accounts.add(b.accountCode);
                });
            } else {
                // Fallback: use sourceAccount/destinationAccount if available
                if (r.sourceAccount) accounts.add(r.sourceAccount);
                if (r.destinationAccount) accounts.add(r.destinationAccount);
            }
        });
        return Array.from(accounts).sort().map(a => ({ value: a, label: a }));
    }, [allRecommendations, entityFilterValues]);

    const handleClearFilters = () => {
        setSelectedEntities([{ value: '__ALL__', label: 'ALL ENTITIES' }]);
        setSelectedAccounts([{ value: '__ALL__', label: 'ALL ACCOUNTS' }]);
    };

    // Reset account selection when entity selection changes
    useEffect(() => {
        setSelectedAccounts([{ value: '__ALL__', label: 'ALL ACCOUNTS' }]);
    }, [selectedEntities]);

    const getSelectedEntityOption = (): ReadonlyArray<MultiselectProps.Option> => selectedEntities;
    const getSelectedAccountOption = (): ReadonlyArray<MultiselectProps.Option> => selectedAccounts;

    return (
        <SpaceBetween size="l">
            <Header
                variant="h1"
                description="Daily cash positioning run results — ZBA sweeps, AQ transfers, and variance detection"
            >
                Cash Positioning
            </Header>

            {/* Filters Section — matches prototype layout */}
            <Container
                header={
                    <Header
                        variant="h2"
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={handleClearFilters} variant="link">
                                    Clear All
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={fetchDashboardData}
                                    loading={loading}
                                >
                                    Run
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        Filters
                    </Header>
                }
            >
                <ColumnLayout columns={4}>
                    <FormField label="DATE:">
                        <DatePicker
                            value={date}
                            onChange={({ detail }) => setDate(detail.value)}
                            placeholder="Select date"
                        />
                    </FormField>
                    <FormField label="REGION:">
                        <Select
                            selectedOption={REGION_OPTIONS.find(o => o.value === region) || null}
                            onChange={({ detail }) =>
                                setRegion(detail.selectedOption.value as PositioningRegion)
                            }
                            options={REGION_OPTIONS}
                            placeholder="Select region"
                        />
                    </FormField>
                    <FormField label="ENTITY:">
                        <Multiselect
                            selectedOptions={getSelectedEntityOption()}
                            onChange={({ detail }) => {
                                const opts = detail.selectedOptions as MultiselectProps.Option[];
                                const hasAll = opts.some(o => o.value === '__ALL__');
                                const prevHadAll = selectedEntities.some(o => o.value === '__ALL__');
                                if (hasAll && !prevHadAll) {
                                    // User just checked ALL — select only ALL
                                    setSelectedEntities([{ value: '__ALL__', label: 'ALL ENTITIES' }]);
                                } else if (!hasAll && prevHadAll) {
                                    // User unchecked ALL — keep remaining selections
                                    setSelectedEntities(opts.filter(o => o.value !== '__ALL__'));
                                } else if (hasAll && opts.length > 1) {
                                    // User selected a specific entity while ALL is checked — uncheck ALL
                                    setSelectedEntities(opts.filter(o => o.value !== '__ALL__'));
                                } else {
                                    setSelectedEntities(opts);
                                }
                            }}
                            options={[
                                { value: '__ALL__', label: 'ALL ENTITIES' },
                                ...entityOptions
                            ]}
                            placeholder="All Entities"
                            filteringType="auto"
                            tokenLimit={2}
                        />
                    </FormField>
                    <FormField label="ACCOUNT:">
                        <Multiselect
                            selectedOptions={getSelectedAccountOption()}
                            onChange={({ detail }) => {
                                const opts = detail.selectedOptions as MultiselectProps.Option[];
                                const hasAll = opts.some(o => o.value === '__ALL__');
                                const prevHadAll = selectedAccounts.some(o => o.value === '__ALL__');
                                if (hasAll && !prevHadAll) {
                                    setSelectedAccounts([{ value: '__ALL__', label: 'ALL ACCOUNTS' }]);
                                } else if (!hasAll && prevHadAll) {
                                    setSelectedAccounts(opts.filter(o => o.value !== '__ALL__'));
                                } else if (hasAll && opts.length > 1) {
                                    setSelectedAccounts(opts.filter(o => o.value !== '__ALL__'));
                                } else {
                                    setSelectedAccounts(opts);
                                }
                            }}
                            options={[
                                { value: '__ALL__', label: 'ALL ACCOUNTS' },
                                ...accountOptions
                            ]}
                            placeholder="All Accounts"
                            filteringType="auto"
                            tokenLimit={2}
                        />
                    </FormField>
                </ColumnLayout>
            </Container>

            {error && (
                <Alert type="error" header="Error loading data">
                    {error}
                </Alert>
            )}

            {loading && !summary ? (
                <Container>
                    <Box textAlign="center" padding="l">
                        <Spinner size="large" />
                        <Box variant="p" margin={{ top: 's' }}>
                            Loading cash positioning data...
                        </Box>
                    </Box>
                </Container>
            ) : (
                <>
                    {/* Tabs for ZBA, AQ, Variances, AI Q&A */}
                    <Tabs
                        activeTabId={activeTab}
                        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
                        tabs={[
                            {
                                id: 'zba',
                                label: `ZBA Positioning (${summary?.zbaWireCount ?? 0})`,
                                content: (
                                    <WireRecommendationsTable
                                        region={region}
                                        date={date}
                                        wireType="ZBA_SWEEP"
                                        entityFilter={entityFilterValues}
                                        accountFilter={accountFilterValues}
                                        onDataLoaded={handleZbaDataLoaded}
                                    />
                                ),
                            },
                            {
                                id: 'aq',
                                label: `AQ Positioning (${summary?.aqTransferCount ?? 0})`,
                                content: (
                                    <WireRecommendationsTable
                                        region={region}
                                        date={date}
                                        wireType="AQ_POSITIONING"
                                        entityFilter={entityFilterValues}
                                        accountFilter={accountFilterValues}
                                    />
                                ),
                            },
                            {
                                id: 'variances',
                                label: `Variances (${summary?.varianceCount ?? 0})`,
                                content: <VariancesTable region={region} date={date} />,
                            },
                            {
                                id: 'ai',
                                label: '💬 AI Assistant',
                                content: <AIQueryPanel />,
                            },
                        ]}
                    />
                </>
            )}
        </SpaceBetween>
    );
};

export default CashPositioningDashboard;
